import { AxiosError, CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { clearCompanyDetailsCache, clearUserDetailsCache } from '../../api-utils';
import authApi from '../auth-api';
import {
  CreateUserRequest,
  GenericResult,
  CurrentUserStatusModel,
  UserDetailModel,
  UserEditModel,
  UserSearchModel,
  UserStatusModel,
} from './models';

/**
 * Provides access to the `/user` auth endpoints.
 */
class UserApi {
  private resource = 'user';

  /**
   * Retrieves a user ready for editing.
   */
  getUserForEdit = async (id: number, token?: CancelToken): Promise<UserEditModel | null> => {
    try {
      const response = await authApi.get(
        `${this.resource}/${id}/edit`,
        {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
          cancelToken: token,
          cache: { maxAge: 0 },
        },
      );

      return plainToClass(UserEditModel, response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Retrieves the user creation action for the provided email.
   */
  getUserStatus = async (email: string, token?: CancelToken): Promise<UserStatusModel> => {
    const response = await authApi.get<UserStatusModel>(
      `${this.resource}/status`,
      {
        params: { email },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(UserStatusModel, response.data);
  }

  /**
   * Creates a new user.
   */
  createUser = async (
    payload: CreateUserRequest,
    token?: CancelToken,
  ): Promise<GenericResult> => {
    const response = await authApi.post(
      `${this.resource}`,
      payload,
      { cancelToken: token },
    );

    return plainToClass(GenericResult, response.data);
  }

  /**
   * Retrieves the current user's status.
   */
  getCurrentUserStatus = async (token?: CancelToken): Promise<CurrentUserStatusModel> => {
    const response = await authApi.get<CurrentUserStatusModel>(
      `${this.resource}/currentuserstatus`,
      {
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CurrentUserStatusModel, response.data);
  }

  /**
   * Updates a user.
   */
  updateUser = async (
    id: number,
    model: UserEditModel,
    token?: CancelToken,
  ): Promise<boolean> => {
    try {
      await authApi.put(
        `${this.resource}/${id}`,
        { ...model, userId: id },
        { cancelToken: token },
      );
      // Invalidate user details cache to ensure next fetch is fresh
      await clearUserDetailsCache(id);
      // Invalidate company details cache to ensure next fetch is fresh
      if (model?.companyId != null) {
        await clearCompanyDetailsCache(model.companyId);
      }
      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Retrieves a user's detail (profile + flocks).
   */
  getUserDetail = async (id: number, token?: CancelToken): Promise<UserDetailModel | null> => {
    try {
      const response = await authApi.get(
        `${this.resource}/${id}/detail`,
        {
          cancelToken: token,
          cache: { maxAge: 0 },
        },
      );

      return plainToClass(UserDetailModel, response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Searches users by keyword.
   */
  searchUsers = async (
    keyword: string,
    take = 50,
    token?: CancelToken,
  ): Promise<UserSearchModel[]> => {
    const response = await authApi.get(
      `${this.resource}/search`,
      {
        params: { keyword, take },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(UserSearchModel, response.data as []);
  }
}

export default new UserApi();
